import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import styled from 'styled-components';

import rgba from 'polished/lib/color/rgba';
import { ConfirmationOverlay } from '../../../app/common/components/ConfirmationOverlay';
import Icon from '../../../app/common/components/Icon';
import RadioButton from '../../../app/common/components/RadioButton';
import { TooltipWrapper } from '../../../app/common/components/TooltipWrapper';
import ICONS from '../../../app/common/constants/icons';
import { COLORS, MQS } from '../../../app/common/constants/styles';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { IAddress } from './address.interface';
import { AddressFormatter } from './addressFormatter.service';
import { CountriesType } from '../../../i18n/countries/countriesState';
import { getChallengeData } from '../../../utils/challengerMain';
import { SupportedLanguage } from '../../../config/constants/supportedLanguage.enum';

export const AddressWrapper = styled.li`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background-color: ${COLORS.white};
    border-radius: 5px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
    padding: 32px 44px;

    &:not(:first-child) {
        margin-top: 32px;
    }

    @media (min-width: ${MQS.mq1Max}) {
        flex-direction: row;
    }

    @media (max-width: ${MQS.mq2Min}) {
        font-size: 0.875rem;
        padding: 20px;
    }
`;

export const AddressLine = styled.p`
    margin: 8px 0;
`;

export const ButtonInline = styled.button`
    border: none;
    background-color: transparent;
    color: ${(props: { disabled?: boolean }) =>
        props.disabled ? rgba(COLORS.grey, 0.4) : COLORS.grey};
    cursor: ${(props: { disabled?: boolean }) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

export const CardNumber = styled.span`
    color: ${COLORS.rakutenRed};
    display: inline-block;
`;

export const Left = styled.div`
    @media (min-width: ${MQS.mq1Max}) {
        max-width: 35%;
    }
`;

export const Right = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    @media (min-width: ${MQS.mq1Max}) {
        max-width: 65%;
    }
`;

export const ControlButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
`;

export const BillingNote = styled.div`
    color: ${COLORS.grey4};
    font-size: 0.75rem;
    margin-bottom: 5px;

    @media (max-width: ${MQS.mq2Min}) {
        font-size: 0.6875rem;
    }
`;

export const BillingNotes = styled.div`
    margin-top: 10px;
`;

interface IAddressProps {
    address: IAddress;
    cards?: string[];
    deleteAddress: (addressUuid: string) => void;
    intl: InjectedIntl;
    isOnlyAddress?: boolean;
    onEdit: (address: IAddress) => void;
    setDefaultShipping: (address: IAddress, isShipping: boolean) => void;
    qaId?: string;
    addressTemplate?: string;
    countries: CountriesType;
    locale: SupportedLanguage;
    challengerPid?: string;
}

interface IAddressState {
    deleteConfirmationOpen?: boolean;
    deleteLoading?: boolean;
}

class Address extends React.Component<IAddressProps, IAddressState> {
    constructor(props: IAddressProps) {
        super(props);

        this.state = {
            deleteConfirmationOpen: false,
            deleteLoading: false,
        };

        this.onAcceptDelete = this.onAcceptDelete.bind(this);
        this.onConfirmationClose = this.onConfirmationClose.bind(this);
        this.onCancelDelete = this.onCancelDelete.bind(this);
    }

    public render() {
        const {
            address,
            cards,
            onEdit,
            setDefaultShipping,
            qaId,
            addressTemplate,
            challengerPid,
            locale,
        } = this.props;
        const { deleteConfirmationOpen } = this.state;
        const hasLinkedCards = cards && cards.length > 0;
        const addressComponent = AddressFormatter.formatToReactNode(
            address,
            true,
            false,
            false,
            locale,
            addressTemplate,
        );

        const onDeleteClick = () => {
            this.setState({ deleteConfirmationOpen: true });
        };

        const onEditClick = () => {
            onEdit(address);
        };

        const onSetDefault = (value: boolean) => {
            if (challengerPid) {
                address.challengeData = getChallengeData(challengerPid);
            }
            setDefaultShipping(address, value);
        };

        let shippingLabel;

        shippingLabel = TranslationHelpers.getMessageDescriptor('address.make_default');

        let billingLabel: ReactNode = '';

        if (cards) {
            billingLabel = cards.map((card, index) => (
                <BillingNote key={index} data-qa-id={`billing-for-${index}`}>
                    {TranslationHelpers.getFormattedMessage('address.billing_for_card')}
                    &nbsp;<CardNumber data-qa-id="billing-for-card">{card}</CardNumber>
                </BillingNote>
            ));
        }

        return (
            <AddressWrapper data-qa-id={`address-${qaId}`}>
                {deleteConfirmationOpen && this.deleteConfirmationOverlay}
                <Left className="info">{addressComponent}</Left>
                <Right>
                    <BillingNotes>{billingLabel}</BillingNotes>
                    <ControlButtons>
                        <TooltipWrapper
                            id={`set-is-shipping-${address.addressUuid}`}
                            tooltipText={TranslationHelpers.getMessageDescriptor(
                                address.isShipping
                                    ? 'tooltip.default_address.activated'
                                    : 'tooltip.default_address.deactivated',
                            )}
                        >
                            <RadioButton
                                id="isShipping"
                                label={shippingLabel}
                                value={address.isShipping}
                                onCheck={() => onSetDefault(true)}
                                qaId="is-shipping"
                            />
                        </TooltipWrapper>
                        <ButtonInline title="Edit" onClick={onEditClick} data-qa-id="edit-address">
                            <Icon icon={ICONS.EDIT} />
                        </ButtonInline>
                        <TooltipWrapper
                            id={`delete-address-${address.addressUuid}`}
                            disableTooltip={!hasLinkedCards}
                            tooltipText={TranslationHelpers.getMessageDescriptor(
                                'tooltip.delete_address_linked_to_cards',
                            )}
                        >
                            <ButtonInline
                                title="Delete"
                                onClick={onDeleteClick}
                                disabled={hasLinkedCards}
                                data-qa-id="delete-address"
                            >
                                <Icon icon={ICONS.TRUSH} />
                            </ButtonInline>
                        </TooltipWrapper>
                    </ControlButtons>
                </Right>
            </AddressWrapper>
        );
    }

    private get deleteConfirmationOverlay() {
        const { address, isOnlyAddress } = this.props;
        const { deleteConfirmationOpen, deleteLoading } = this.state;

        if (!deleteConfirmationOpen) {
            return '';
        }

        const warningTextTop = address.isShipping
            ? TranslationHelpers.getFormattedMessage(
                  'address.delete_confirmation.warning_top_default',
              )
            : TranslationHelpers.getFormattedMessage('address.delete_confirmation.warning_top');

        return (
            <ConfirmationOverlay
                title={TranslationHelpers.getMessageDescriptor('address.delete')}
                isOpen={deleteConfirmationOpen}
                onAccept={this.onAcceptDelete}
                onCancel={this.onCancelDelete}
                onClose={this.onConfirmationClose}
                confirmButtonText={TranslationHelpers.getMessageDescriptor('button.delete')}
            >
                {warningTextTop}
                {AddressFormatter.formatToReactNode(address, true, true)}
            </ConfirmationOverlay>
        );
    }

    private onConfirmationClose() {
        this.setState({ deleteConfirmationOpen: false });
    }

    private onAcceptDelete() {
        const { address, deleteAddress } = this.props;
        deleteAddress(address.addressUuid);
        this.setState({ deleteConfirmationOpen: false });
    }

    private onCancelDelete() {
        this.setState({ deleteConfirmationOpen: false });
    }
}

export default injectIntl(Address);
